// src/features/notification/notificationActions.js
import {
  getNotifications,
  getUnreadCount,
  markNotificationAsRead as markNotificationAsReadApi,
  markAllNotificationsAsRead as markAllNotificationsAsReadApi,
  markMultipleNotificationsAsRead as markMultipleNotificationsAsReadApi,
  deleteNotification as deleteNotificationApi,
  deleteMultipleNotifications as deleteMultipleNotificationsApi,
  getNotificationPreferences,
  updateNotificationPreferences as updateNotificationPreferencesApi,
} from "../../api/notificationApi";
import notificationCache, {
  CACHE_KEYS,
} from "../../utils/notification/notificationCache";

// Fetch notifications actions
export const fetchNotificationsStart = () => ({
  type: "FETCH_NOTIFICATIONS_START",
});

export const fetchNotificationsSuccess = (
  notifications,
  pagination,
  append = false
) => ({
  type: "FETCH_NOTIFICATIONS_SUCCESS",
  payload: { notifications, pagination, append },
});

export const fetchNotificationsError = (error) => ({
  type: "FETCH_NOTIFICATIONS_ERROR",
  payload: error,
});

// Fetch unread count actions
export const fetchUnreadCountStart = () => ({
  type: "FETCH_UNREAD_COUNT_START",
});

export const fetchUnreadCountSuccess = (count) => ({
  type: "FETCH_UNREAD_COUNT_SUCCESS",
  payload: count,
});

export const fetchUnreadCountError = (error) => ({
  type: "FETCH_UNREAD_COUNT_ERROR",
  payload: error,
});

// Mark notification as read actions
export const markNotificationReadStart = () => ({
  type: "MARK_NOTIFICATION_READ_START",
});

export const markNotificationReadSuccess = (notificationId) => ({
  type: "MARK_NOTIFICATION_READ_SUCCESS",
  payload: { notificationId },
});

export const markNotificationReadError = (error) => ({
  type: "MARK_NOTIFICATION_READ_ERROR",
  payload: error,
});

// Mark all notifications as read actions
export const markAllNotificationsReadStart = () => ({
  type: "MARK_ALL_NOTIFICATIONS_READ_START",
});

export const markAllNotificationsReadSuccess = () => ({
  type: "MARK_ALL_NOTIFICATIONS_READ_SUCCESS",
});

export const markAllNotificationsReadError = (error) => ({
  type: "MARK_ALL_NOTIFICATIONS_READ_ERROR",
  payload: error,
});

// Mark multiple notifications as read actions
export const markMultipleNotificationsReadStart = () => ({
  type: "MARK_MULTIPLE_NOTIFICATIONS_READ_START",
});

export const markMultipleNotificationsReadSuccess = (notificationIds) => ({
  type: "MARK_MULTIPLE_NOTIFICATIONS_READ_SUCCESS",
  payload: { notificationIds },
});

export const markMultipleNotificationsReadError = (error) => ({
  type: "MARK_MULTIPLE_NOTIFICATIONS_READ_ERROR",
  payload: error,
});

// Delete notification actions
export const deleteNotificationStart = () => ({
  type: "DELETE_NOTIFICATION_START",
});

export const deleteNotificationSuccess = (notificationId) => ({
  type: "DELETE_NOTIFICATION_SUCCESS",
  payload: { notificationId },
});

export const deleteNotificationError = (error) => ({
  type: "DELETE_NOTIFICATION_ERROR",
  payload: error,
});

// Delete multiple notifications actions
export const deleteMultipleNotificationsStart = () => ({
  type: "DELETE_MULTIPLE_NOTIFICATIONS_START",
});

export const deleteMultipleNotificationsSuccess = (notificationIds) => ({
  type: "DELETE_MULTIPLE_NOTIFICATIONS_SUCCESS",
  payload: { notificationIds },
});

export const deleteMultipleNotificationsError = (error) => ({
  type: "DELETE_MULTIPLE_NOTIFICATIONS_ERROR",
  payload: error,
});

// Notification preferences actions
export const fetchNotificationPreferencesStart = () => ({
  type: "FETCH_NOTIFICATION_PREFERENCES_START",
});

export const fetchNotificationPreferencesSuccess = (preferences) => ({
  type: "FETCH_NOTIFICATION_PREFERENCES_SUCCESS",
  payload: preferences,
});

export const fetchNotificationPreferencesError = (error) => ({
  type: "FETCH_NOTIFICATION_PREFERENCES_ERROR",
  payload: error,
});

export const updateNotificationPreferencesStart = () => ({
  type: "UPDATE_NOTIFICATION_PREFERENCES_START",
});

export const updateNotificationPreferencesSuccess = (preferences) => ({
  type: "UPDATE_NOTIFICATION_PREFERENCES_SUCCESS",
  payload: preferences,
});

export const updateNotificationPreferencesError = (error) => ({
  type: "UPDATE_NOTIFICATION_PREFERENCES_ERROR",
  payload: error,
});

// Filters and pagination actions
export const setNotificationFilters = (filters) => ({
  type: "SET_NOTIFICATION_FILTERS",
  payload: filters,
});

export const setNotificationPage = (page) => ({
  type: "SET_NOTIFICATION_PAGE",
  payload: page,
});

// Selection actions
export const selectNotification = (notificationId) => ({
  type: "SELECT_NOTIFICATION",
  payload: notificationId,
});

export const selectAllNotifications = (selected) => ({
  type: "SELECT_ALL_NOTIFICATIONS",
  payload: selected,
});

export const clearNotificationSelection = () => ({
  type: "CLEAR_NOTIFICATION_SELECTION",
});

// Real-time update actions
export const addNewNotification = (notification) => ({
  type: "ADD_NEW_NOTIFICATION",
  payload: notification,
});

export const updateNotification = (notification) => ({
  type: "UPDATE_NOTIFICATION",
  payload: notification,
});

export const removeNotification = (notificationId) => ({
  type: "REMOVE_NOTIFICATION",
  payload: notificationId,
});

// Polling control actions
export const setPollingEnabled = (enabled) => ({
  type: "SET_POLLING_ENABLED",
  payload: enabled,
});

// Error handling actions
export const clearNotificationError = (errorType) => ({
  type: "CLEAR_NOTIFICATION_ERROR",
  payload: errorType,
});

export const clearAllNotificationErrors = () => ({
  type: "CLEAR_ALL_NOTIFICATION_ERRORS",
});

// Reset state action
export const resetNotificationState = () => ({
  type: "RESET_NOTIFICATION_STATE",
});

// Thunk actions for async operations

/**
 * Fetch notifications with pagination and filters
 */
export const fetchNotifications = (
  params = {},
  append = false,
  useCache = true,
  providedToken = null
) => {
  return async (dispatch, getState) => {
    dispatch(fetchNotificationsStart());

    try {
      // Always use the provided token (from Auth0's getAccessTokenSilently)
      if (!providedToken) {
        console.log(
          "🔐 No authentication token provided for notification fetch"
        );
        dispatch(fetchNotificationsError("Authentication token required"));
        return;
      }

      const token = providedToken;

      const response = await getNotifications(params, token, useCache);
      dispatch(
        fetchNotificationsSuccess(
          response.data.notifications,
          response.data.pagination,
          append
        )
      );
      return response;
    } catch (error) {
      // Try to load from cache if network request fails
      if (useCache && navigator.onLine === false) {
        try {
          const cacheKey = `${CACHE_KEYS.NOTIFICATIONS}_${JSON.stringify(
            params
          )}`;
          const cachedData = notificationCache.get(cacheKey);
          if (cachedData && cachedData.data) {
            dispatch(
              fetchNotificationsSuccess(
                cachedData.data.notifications,
                cachedData.data.pagination,
                append
              )
            );
            return cachedData;
          }
        } catch (cacheError) {
          console.warn("Failed to load from cache:", cacheError);
        }
      }

      const errorMessage = error.message || "Failed to fetch notifications";
      dispatch(fetchNotificationsError(errorMessage));

      // Don't throw auth-related errors to prevent uncaught promise rejections
      if (
        errorMessage.includes("Authentication") ||
        errorMessage.includes("token")
      ) {
        console.log(
          "🔐 Authentication error in notification fetch:",
          errorMessage
        );
        return;
      }

      throw error;
    }
  };
};

/**
 * Fetch unread notification count
 */
export const fetchUnreadCount = (useCache = true, providedToken = null) => {
  return async (dispatch, getState) => {
    dispatch(fetchUnreadCountStart());

    try {
      // Always use the provided token (from Auth0's getAccessTokenSilently)
      if (!providedToken) {
        console.log(
          "🔐 No authentication token provided for notification fetch"
        );
        dispatch(fetchUnreadCountError("Authentication token required"));
        return;
      }

      const token = providedToken;

      const response = await getUnreadCount(token, useCache);
      dispatch(fetchUnreadCountSuccess(response.data.unreadCount));
      return response;
    } catch (error) {
      // Try to load from cache if network request fails
      if (useCache && navigator.onLine === false) {
        try {
          const cachedCount = notificationCache.get(CACHE_KEYS.UNREAD_COUNT);
          if (cachedCount !== null) {
            dispatch(fetchUnreadCountSuccess(cachedCount));
            return { data: { unreadCount: cachedCount } };
          }
        } catch (cacheError) {
          console.warn("Failed to load unread count from cache:", cacheError);
        }
      }

      const errorMessage = error.message || "Failed to fetch unread count";
      dispatch(fetchUnreadCountError(errorMessage));

      // Don't throw auth-related errors to prevent uncaught promise rejections
      if (
        errorMessage.includes("Authentication") ||
        errorMessage.includes("token")
      ) {
        console.log(
          "🔐 Authentication error in notification fetch:",
          errorMessage
        );
        return;
      }

      throw error;
    }
  };
};

/**
 * Mark a single notification as read
 */
export const markNotificationAsRead = (
  notificationId,
  providedToken = null
) => {
  return async (dispatch, getState) => {
    dispatch(markNotificationReadStart());

    try {
      if (!providedToken) {
        throw new Error("Authentication token required");
      }
      const token = providedToken;

      await markNotificationAsReadApi(notificationId, token);
      dispatch(markNotificationReadSuccess(notificationId));
    } catch (error) {
      dispatch(
        markNotificationReadError(
          error.message || "Failed to mark notification as read"
        )
      );
      throw error;
    }
  };
};

/**
 * Mark all notifications as read
 */
export const markAllNotificationsAsRead = (providedToken = null) => {
  return async (dispatch, getState) => {
    dispatch(markAllNotificationsReadStart());

    try {
      if (!providedToken) {
        throw new Error("Authentication token required");
      }
      const token = providedToken;

      await markAllNotificationsAsReadApi(token);
      dispatch(markAllNotificationsReadSuccess());
    } catch (error) {
      dispatch(
        markAllNotificationsReadError(
          error.message || "Failed to mark all notifications as read"
        )
      );
      throw error;
    }
  };
};

/**
 * Mark multiple notifications as read (bulk operation)
 */
export const markMultipleNotificationsAsRead = (
  notificationIds,
  providedToken = null
) => {
  return async (dispatch, getState) => {
    dispatch(markMultipleNotificationsReadStart());

    try {
      if (!providedToken) {
        throw new Error("Authentication token required");
      }
      const token = providedToken;

      await markMultipleNotificationsAsReadApi(notificationIds, token);
      dispatch(markMultipleNotificationsReadSuccess(notificationIds));
    } catch (error) {
      dispatch(
        markMultipleNotificationsReadError(
          error.message || "Failed to mark notifications as read"
        )
      );
      throw error;
    }
  };
};

/**
 * Delete a single notification
 */
export const deleteNotification = (notificationId, providedToken = null) => {
  return async (dispatch, getState) => {
    dispatch(deleteNotificationStart());

    try {
      if (!providedToken) {
        throw new Error("Authentication token required");
      }
      const token = providedToken;

      await deleteNotificationApi(notificationId, token);
      dispatch(deleteNotificationSuccess(notificationId));
    } catch (error) {
      dispatch(
        deleteNotificationError(
          error.message || "Failed to delete notification"
        )
      );
      throw error;
    }
  };
};

/**
 * Delete multiple notifications (bulk operation)
 */
export const deleteMultipleNotifications = (
  notificationIds,
  providedToken = null
) => {
  return async (dispatch, getState) => {
    dispatch(deleteMultipleNotificationsStart());

    try {
      if (!providedToken) {
        throw new Error("Authentication token required");
      }
      const token = providedToken;

      await deleteMultipleNotificationsApi(notificationIds, token);
      dispatch(deleteMultipleNotificationsSuccess(notificationIds));
    } catch (error) {
      dispatch(
        deleteMultipleNotificationsError(
          error.message || "Failed to delete notifications"
        )
      );
      throw error;
    }
  };
};

/**
 * Fetch notification preferences
 */
export const fetchNotificationPreferences = (token = null) => {
  return async (dispatch, getState) => {
    dispatch(fetchNotificationPreferencesStart());

    try {
      // Use provided token
      if (!token) {
        throw new Error("Authentication token required");
      }
      const authToken = token;

      const response = await getNotificationPreferences(authToken);
      dispatch(fetchNotificationPreferencesSuccess(response.data));
      return response;
    } catch (error) {
      dispatch(
        fetchNotificationPreferencesError(
          error.message || "Failed to fetch preferences"
        )
      );
      throw error;
    }
  };
};

/**
 * Update notification preferences
 */
export const updateNotificationPreferences = (preferences, token = null) => {
  return async (dispatch, getState) => {
    dispatch(updateNotificationPreferencesStart());

    try {
      // Use provided token
      if (!token) {
        throw new Error("Authentication token required");
      }
      const authToken = token;

      const response = await updateNotificationPreferencesApi(
        preferences,
        authToken
      );
      dispatch(updateNotificationPreferencesSuccess(response.data));
      return response;
    } catch (error) {
      dispatch(
        updateNotificationPreferencesError(
          error.message || "Failed to update preferences"
        )
      );
      throw error;
    }
  };
};
